import { useState, useEffect } from 'react'
import { CreditCard, Save, ToggleLeft, ToggleRight, RefreshCw } from 'lucide-react'
import { EmptyState } from '@/components/ui/EmptyState'
import { SkeletonTable } from '@/components/ui/Skeleton'
import { usePermissions } from '@/hooks/usePermissions'
import { Navigate } from 'react-router-dom'
import plansService, { Plan, UpdatePlanInput } from '@/services/plansService'
import { toast } from 'sonner'

export default function Plans() {
  const { isAdmin, isGod } = usePermissions()
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const [editingPlan, setEditingPlan] = useState<string | null>(null)
  const [editData, setEditData] = useState<UpdatePlanInput>({})
  const [saving, setSaving] = useState(false)

  // Guard
  if (!isAdmin() && !isGod()) {
    return <Navigate to="/" replace />
  }

  // Carregar planos
  const loadPlans = async () => {
    try {
      setLoading(true)
      const data = await plansService.listPlans()
      setPlans(data)
    } catch (error) {
      console.error('Erro ao carregar planos:', error)
      toast.error('Erro ao carregar planos')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPlans()
  }, [])

  // Iniciar edição
  const startEditing = (plan: Plan) => {
    setEditingPlan(plan.id)
    setEditData({
      name: plan.name,
      description: plan.description || '',
      dailyTokenLimit: plan.dailyTokenLimit,
      weeklyTokenLimit: plan.weeklyTokenLimit,
      monthlyTokenLimit: plan.monthlyTokenLimit,
      isActive: plan.isActive,
      displayOrder: plan.displayOrder,
    })
  }

  // Cancelar edição
  const cancelEditing = () => {
    setEditingPlan(null)
    setEditData({})
  }

  // Salvar edição
  const handleSave = async (planId: string) => {
    try {
      setSaving(true)
      await plansService.updatePlan(planId, editData)
      toast.success('Plano atualizado com sucesso')
      setEditingPlan(null)
      setEditData({})
      loadPlans()
    } catch (error) {
      console.error('Erro ao atualizar plano:', error)
      toast.error('Erro ao atualizar plano')
    } finally {
      setSaving(false)
    }
  }

  // Toggle ativo/inativo
  const handleToggleActive = async (plan: Plan) => {
    try {
      await plansService.updatePlan(plan.id, { isActive: !plan.isActive })
      toast.success(`Plano ${!plan.isActive ? 'ativado' : 'desativado'}`)
      loadPlans()
    } catch (error) {
      console.error('Erro ao alterar status do plano:', error)
      toast.error('Erro ao alterar status')
    }
  }

  const formatNumber = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return 'Ilimitado'
    return value.toLocaleString('pt-BR')
  }

  const renderEditableField = (
    label: string,
    field: keyof UpdatePlanInput,
    type: 'text' | 'number' = 'number'
  ) => {
    const value = editData[field]
    return (
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
        {type === 'number' ? (
          <input
            type="number"
            value={value === null || value === undefined ? '' : String(value)}
            onChange={(e) => {
              const val = e.target.value === '' ? null : Number(e.target.value)
              setEditData(prev => ({ ...prev, [field]: val }))
            }}
            placeholder="Ilimitado"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        ) : (
          <input
            type="text"
            value={String(value || '')}
            onChange={(e) => setEditData(prev => ({ ...prev, [field]: e.target.value }))}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciamento de Planos</h1>
          <p className="text-sm text-gray-600 mt-1">Configure os limites e recursos de cada plano de assinatura</p>
        </div>
        <button onClick={loadPlans} className="btn btn-secondary w-full sm:w-auto">
          <RefreshCw className="mr-2 h-4 w-4" />
          Atualizar
        </button>
      </div>

      {/* Plans Grid */}
      {loading ? (
        <SkeletonTable rows={4} columns={6} />
      ) : plans.length === 0 ? (
        <EmptyState
          icon={CreditCard}
          title="Nenhum plano encontrado"
          description="Os planos serão criados automaticamente ao executar as migrations."
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {plans
            .filter(p => p.id.startsWith('plan_'))
            .sort((a, b) => a.displayOrder - b.displayOrder)
            .map((plan) => (
              <div
                key={plan.id}
                className={`bg-white rounded-lg shadow border-2 transition-colors ${plan.isActive ? 'border-green-200' : 'border-gray-200 opacity-70'
                  } ${plan.isDefault ? 'ring-2 ring-blue-400' : ''}`}
              >
                {/* Card Header */}
                <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${plan.isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {editingPlan === plan.id ? (
                          <input
                            type="text"
                            value={editData.name || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                            className="px-2 py-1 text-sm border border-gray-300 rounded-md"
                          />
                        ) : (
                          plan.name
                        )}
                      </h3>
                      <span className="text-xs text-gray-500">{plan.slug}</span>
                      {plan.isDefault && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Padrão</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(plan)}
                      className={`p-1.5 rounded-md transition-colors ${plan.isActive
                        ? 'text-green-600 hover:bg-green-50'
                        : 'text-gray-400 hover:bg-gray-50'
                        }`}
                      title={plan.isActive ? 'Desativar plano' : 'Ativar plano'}
                    >
                      {plan.isActive ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-4 space-y-4">
                  {editingPlan === plan.id ? (
                    /* Edit Mode */
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        {renderEditableField('Tokens / Dia', 'dailyTokenLimit')}
                        {renderEditableField('Tokens / Semana', 'weeklyTokenLimit')}
                        {renderEditableField('Tokens / Mês', 'monthlyTokenLimit')}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Descrição</label>
                        <textarea
                          value={editData.description || ''}
                          onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                          rows={2}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={() => handleSave(plan.id)}
                          disabled={saving}
                          className="flex-1 btn btn-primary text-sm py-2"
                        >
                          <Save className="mr-1 h-4 w-4" />
                          {saving ? 'Salvando...' : 'Salvar'}
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="flex-1 btn btn-secondary text-sm py-2"
                        >
                          Cancelar
                        </button>
                      </div>
                    </>
                  ) : (
                    /* View Mode */
                    <>
                      <p className="text-sm text-gray-600">{plan.description || 'Sem descrição'}</p>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 rounded-md p-3">
                          <div className="text-xs text-gray-500">Tokens / Dia</div>
                          <div className="text-sm font-semibold text-gray-900">{formatNumber(plan.dailyTokenLimit)}</div>
                        </div>
                        <div className="bg-gray-50 rounded-md p-3">
                          <div className="text-xs text-gray-500">Tokens / Semana</div>
                          <div className="text-sm font-semibold text-gray-900">{formatNumber(plan.weeklyTokenLimit)}</div>
                        </div>
                        <div className="bg-gray-50 rounded-md p-3">
                          <div className="text-xs text-gray-500">Tokens / Mês</div>
                          <div className="text-sm font-semibold text-gray-900">{formatNumber(plan.monthlyTokenLimit)}</div>
                        </div>
                      </div>

                      <button
                        onClick={() => startEditing(plan)}
                        className="w-full btn btn-secondary text-sm py-2 mt-2"
                      >
                        Editar Limites
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  )
}
